/**
 * Grille des 10 paliers de producteur.
 *
 * Reprise de la section 5 du dossier de synthèse et de l'onglet
 * « Grille finale (retenue) » de la simulation de rentabilité.
 *
 * Tous les montants sont en centimes entiers. Le chiffre d'affaires et les
 * abonnements ne doivent jamais transiter en nombres flottants : `0.1 + 0.2`
 * ne vaut pas `0.3` en binaire, et un écart d'un centime sur un reversement est
 * un écart comptable.
 */

export interface ProducerTier {
  /** Rang de 1 à 10. */
  readonly level: number;
  readonly title: string;
  /** Chiffre d'affaires mensuel à partir duquel le palier s'applique. */
  readonly revenueFromCents: number;
  /** Plafond du palier. `undefined` pour le dernier, ouvert vers le haut. */
  readonly revenueToCents: number | undefined;
  readonly subscriptionCents: number;
  /** Photos par produit. Toujours un entier : pas d'illimité. */
  readonly maxPhotosPerProduct: number;
  /**
   * Produits publiés simultanément. `undefined` : toute la gamme de la boutique,
   * sans plafond plateforme (à partir du palier établi).
   */
  readonly maxActiveProducts: number | undefined;
  /** Fonctionnalité que le palier débloque, telle qu'annoncée au producteur. */
  readonly flagshipFeature: string;
}

export const PRODUCER_TIERS: readonly ProducerTier[] = [
  {
    level: 1,
    title: 'Découvreur',
    revenueFromCents: 0,
    revenueToCents: 10_000,
    subscriptionCents: 0,
    maxPhotosPerProduct: 1,
    maxActiveProducts: 5,
    flagshipFeature: 'Test gratuit — 1 photo par produit, 5 produits',
  },
  {
    level: 2,
    title: 'Petit Producteur',
    revenueFromCents: 10_000,
    revenueToCents: 25_000,
    subscriptionCents: 300,
    maxPhotosPerProduct: 3,
    maxActiveProducts: 15,
    flagshipFeature: 'Section « Notre histoire », badge, et services sur produit',
  },
  {
    level: 3,
    title: 'Producteur Confirmé',
    revenueFromCents: 25_000,
    revenueToCents: 50_000,
    subscriptionCents: 400,
    maxPhotosPerProduct: 5,
    maxActiveProducts: 30,
    flagshipFeature: 'Bannière personnalisée et un code promo actif',
  },
  {
    level: 4,
    title: 'Producteur Établi',
    revenueFromCents: 50_000,
    revenueToCents: 90_000,
    subscriptionCents: 600,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'Boutique à votre image (modèle)',
  },
  {
    level: 5,
    title: 'Producteur Expert',
    revenueFromCents: 90_000,
    revenueToCents: 150_000,
    subscriptionCents: 900,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'Vidéo sur chaque fiche produit',
  },
  {
    level: 6,
    title: 'Artisan Reconnu',
    revenueFromCents: 150_000,
    revenueToCents: 250_000,
    subscriptionCents: 1_100,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'Analyse de marché de votre secteur',
  },
  {
    level: 7,
    title: 'Maître Producteur',
    revenueFromCents: 250_000,
    revenueToCents: 400_000,
    subscriptionCents: 1_500,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'Proposition de prix maximisé',
  },
  {
    level: 8,
    title: 'Producteur Vétéran',
    revenueFromCents: 400_000,
    revenueToCents: 600_000,
    subscriptionCents: 1_900,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'En-tête boutique et sous-domaine métier',
  },
  {
    level: 9,
    title: 'Légende Locale',
    revenueFromCents: 600_000,
    revenueToCents: 900_000,
    subscriptionCents: 2_400,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'Vitrine du mois',
  },
  {
    level: 10,
    title: 'Champion du Terroir',
    revenueFromCents: 900_000,
    revenueToCents: undefined,
    subscriptionCents: 3_000,
    maxPhotosPerProduct: 10,
    maxActiveProducts: undefined,
    flagshipFeature: 'Marque blanche et nom de domaine',
  },
];

/** Palier à partir duquel le producteur est considéré comme installé. */
export const ESTABLISHED_TIER_LEVEL = 4;
/** Services complémentaires sur un produit (épluché, tranché…). */
export const EXTRAS_TIER_LEVEL = 2;
/** Une vidéo par fiche produit. */
export const VIDEO_TIER_LEVEL = 5;
/** Analyse de marché (comparaison anonymisée du secteur). */
export const MARKET_ANALYSIS_TIER_LEVEL = 6;
/** Proposition de prix maximisé à partir des coûts et du marché. */
export const MAXIMIZED_PRICE_TIER_LEVEL = 7;
/** Astuces de pilotage de la boutique. */
export const SHOP_TIPS_TIER_LEVEL = 8;

export const FIRST_TIER = PRODUCER_TIERS[0] as ProducerTier;

/**
 * Palier correspondant à un chiffre d'affaires mensuel.
 *
 * Simple consultation de la grille. La règle asymétrique du dossier — un
 * producteur monte automatiquement mais ne redescend que sur demande validée,
 * pour qu'un pic de vente ponctuel ne le pénalise pas le mois suivant — relève
 * du lot 7 et ne peut pas être décidée ici : elle dépend du palier précédent.
 */
export function tierForMonthlyRevenue(revenueCents: number): ProducerTier {
  let match = FIRST_TIER;
  for (const tier of PRODUCER_TIERS) {
    if (revenueCents >= tier.revenueFromCents) {
      match = tier;
    }
  }
  return match;
}

export function tierByLevel(level: number): ProducerTier | undefined {
  return PRODUCER_TIERS.find((tier) => tier.level === level);
}

/** Palier suivant, ou `undefined` au sommet de la grille. */
export function nextTier(level: number): ProducerTier | undefined {
  return tierByLevel(level + 1);
}

const EUR_WHOLE = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const EUR_WITH_CENTS = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formate des centimes en euros à la française : « 4,90 € », « 1 240 € ».
 *
 * Un montant monétaire s'écrit avec zéro ou deux décimales, jamais une seule :
 * « 4,9 € » se lit comme une valeur approchée. `Intl` ne sait pas exprimer
 * « zéro ou deux » en une seule configuration, d'où les deux formateurs — un
 * pour les montants ronds, où les centimes seraient du bruit, l'autre pour les
 * prix au détail.
 */
export function formatEurFromCents(cents: number): string {
  const formatter = cents % 100 === 0 ? EUR_WHOLE : EUR_WITH_CENTS;
  return formatter.format(cents / 100);
}

/** Abonnement standard du palier, sans majoration. */
export function standardSubscriptionCents(level: number): number {
  return tierByLevel(level)?.subscriptionCents ?? 0;
}

/**
 * Abonnement d'un palier débloqué avant d'en avoir le CA : tarif visé + 100 %
 * de l'écart. Palier 3 (4 €) → 4 (6 €) : 8 €. Palier 3 → 5 (9 €) : 14 €.
 */
export function boostedSubscriptionCents(fromLevel: number, toLevel: number): number {
  const from = standardSubscriptionCents(fromLevel);
  const to = standardSubscriptionCents(toLevel);
  if (toLevel <= fromLevel) {
    return to;
  }
  return to + (to - from);
}

export interface DowngradeLosses {
  readonly fromLevel: number;
  readonly toLevel: number;
  readonly productCap: number | undefined;
  readonly photoCap: number | undefined;
  readonly lostFeatures: readonly string[];
}

/** Ce que la boutique perd en descendant de palier. */
export function downgradeLosses(fromLevel: number, toLevel: number): DowngradeLosses {
  const target = tierByLevel(toLevel);
  const lostFeatures: string[] = [];
  for (let level = fromLevel; level > toLevel; level -= 1) {
    const feature = tierByLevel(level)?.flagshipFeature;
    if (feature !== undefined && level >= 2) {
      lostFeatures.push(feature);
    }
  }
  return {
    fromLevel,
    toLevel,
    productCap: target?.maxActiveProducts,
    photoCap: target?.maxPhotosPerProduct,
    lostFeatures,
  };
}

/** Libellé du 1er du mois UTC : « 1er septembre 2026 ». */
export function formatFirstOfMonth(iso: string): string {
  const date = new Date(iso);
  const month = new Intl.DateTimeFormat('fr-FR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  return `1er ${month}`;
}
